import { useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { Replayer } from 'rrweb';
import 'rrweb/dist/style.css';
import { Box, Typography } from '@mui/material';
import { REPLAY_ANIMATION_RESET } from '@replayops/replay-core';

export interface HeatPoint {
  x: number;
  y: number;
  weight: number;
}

interface Props {
  points: HeatPoint[];
  /** Logical width/height of the captured viewport the points live in. */
  width?: number;
  height?: number;
  /**
   * Optional rrweb events of a representative session; when provided the page's
   * reconstructed DOM is rendered behind the heat overlay (FullStory-style).
   */
  backgroundEvents?: unknown[] | null;
  /** Click on the stage → logical (x,y) in the captured viewport, for drill-down. */
  onRegionClick?: (x: number, y: number) => void;
}

/**
 * Whether a recording contains a full DOM snapshot — rrweb event type 2, the
 * only event that carries a whole page. Without one there is nothing for the
 * replayer to build a backdrop from.
 */
function hasFullSnapshot(events: unknown[] | null | undefined): boolean {
  if (!events || events.length < 2) return false;
  return events.some((e) => (e as { type?: number } | null)?.type === 2);
}

/** Maps a normalized intensity (0..1) to a blue→green→yellow→red heat colour. */
function heatColor(t: number): [number, number, number] {
  const stops: [number, [number, number, number]][] = [
    [0.0, [30, 60, 200]],
    [0.4, [40, 200, 120]],
    [0.7, [240, 220, 40]],
    [1.0, [230, 30, 40]],
  ];
  for (let i = 1; i < stops.length; i++) {
    const [p1, c1] = stops[i - 1]!;
    const [p2, c2] = stops[i]!;
    if (t <= p2) {
      const f = (t - p1) / (p2 - p1 || 1);
      return [
        Math.round(c1[0] + (c2[0] - c1[0]) * f),
        Math.round(c1[1] + (c2[1] - c1[1]) * f),
        Math.round(c1[2] + (c2[2] - c1[2]) * f),
      ];
    }
  }
  return stops[stops.length - 1]![1];
}

/**
 * Canvas click-heatmap with an optional reconstructed-page backdrop. The heat
 * layer accumulates click weights into an intensity buffer via additive radial
 * gradients, then colour-maps them. Both the backdrop (rrweb) and the canvas
 * live in one logical-sized "stage" that is CSS-scaled to fit the container, so
 * heat points align pixel-for-pixel with the page they were captured on.
 */
export function Heatmap({ points, width = 1440, height = 900, backgroundEvents, onRegionClick }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bgRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Scale the logical stage down to the container width.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / width));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);

  // Render the representative session's initial DOM as the backdrop.
  useEffect(() => {
    const root = bgRef.current;
    if (!root) return;
    root.innerHTML = '';
    // A recording with no full snapshot in it has no page to draw. Length is
    // not the test: a session that was opened and closed carries a couple of
    // meta events and passes it, then renders as a blank backdrop under the
    // heat — which looks like the heatmap is broken rather than like the
    // recording being unusable.
    if (!hasFullSnapshot(backgroundEvents)) return;
    try {
      const replayer = new Replayer(backgroundEvents as never[], {
        root,
        skipInactive: true,
        mouseTail: false,
        showWarning: false,
        // Neutralise CSS entrance animations so pages that fade their content in
        // (opacity:0 → 1) don't render blank behind the heat overlay.
        insertStyleRules: REPLAY_ANIMATION_RESET,
      });
      // Seek past the initial paint so the DOM + styles have fully settled.
      replayer.pause(1200);
    } catch {
      root.innerHTML = '';
    }
  }, [backgroundEvents]);

  // Draw the heat overlay.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    canvas.width = width;
    canvas.height = height;
    ctx.clearRect(0, 0, width, height);
    if (points.length === 0) return;

    const maxWeight = Math.max(...points.map((p) => p.weight));
    const radius = 40;
    ctx.globalCompositeOperation = 'lighter';
    for (const p of points) {
      const intensity = p.weight / maxWeight;
      const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, radius);
      g.addColorStop(0, `rgba(0,0,0,${0.15 + 0.6 * intensity})`);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(p.x, p.y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'source-over';
    const img = ctx.getImageData(0, 0, width, height);
    const data = img.data;
    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3]!;
      if (alpha === 0) continue;
      const t = Math.min(1, alpha / 255);
      const [r, g, b] = heatColor(t);
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
      data[i + 3] = Math.round(190 * t);
    }
    ctx.putImageData(img, 0, 0);
  }, [points, width, height]);

  const handleClick = (e: ReactMouseEvent) => {
    if (!onRegionClick) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    onRegionClick(Math.round((e.clientX - rect.left) / scale), Math.round((e.clientY - rect.top) / scale));
  };

  return (
    <Box
      ref={containerRef}
      onClick={handleClick}
      sx={{
        position: 'relative',
        width: '100%',
        height: height * scale,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        // White page behind the backdrop; a subtle neutral when showing heat alone.
        bgcolor: backgroundEvents ? '#fff' : 'action.hover',
        cursor: onRegionClick && points.length ? 'crosshair' : 'default',
      }}
    >
      {points.length === 0 && (
        <Typography
          color="text.secondary"
          sx={{ position: 'absolute', top: 16, left: 16, zIndex: 3 }}
        >
          No click data for this URL yet.
        </Typography>
      )}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width,
          height,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <Box
          ref={bgRef}
          sx={{
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            // rrweb centres its wrapper by default — pin it to the stage origin
            // so heat points align with the reconstructed page.
            '& .replayer-wrapper': { position: 'absolute', top: 0, left: 0, transform: 'none' },
            '& iframe': { border: 'none', background: '#fff' },
            '& .replayer-mouse, & .replayer-mouse-tail': { display: 'none' },
          }}
        />
        <canvas
          ref={canvasRef}
          style={{ position: 'absolute', top: 0, left: 0, width, height, pointerEvents: 'none' }}
        />
      </Box>
    </Box>
  );
}
